import type moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { NotificationStatus } from '../../types/notification-status';
import type {
    NotificationCodeState,
    NotificationContractRowState,
    NotificationDropOffPointState,
    NotificationEmailState,
    NotificationEquipmentState,
    NotificationHistoryEntryState,
    NotificationLongTextLineState,
    NotificationPaperworkQuestionState,
    NotificationParticipantState,
    NotificationPartState,
    NotificationTaskState,
} from './notification-entities.state';
import { LoadStatus } from '../load-status';
import { NotificationIdState } from '../utilities/notification-id.type';

export const NOTIFICATION_FEATURE_KEY = 'notification';

/**
 * How long a completion record is kept: a year. Long enough that a notification the backend sends
 * again without CRM — its confirmation failed to process there — is still recognized as submitted
 * and cannot be processed a second time.
 */
export const COMPLETION_RETENTION_MS = 365 * 24 * 60 * 60 * 1000;

/** One status the notification has been recorded in, in serializable form. */
export type NotificationStatusRecordState = {
    statusCode: string;
    startTime: string;
    endTime: string;
};

/**
 * The notification's time zone, in the SAP wire form: the offset as an ISO 8601 duration
 * (`'PT02H00M00S'`), its sign, and the zone's name. Kept as strings because that is what a
 * `moment.Duration` would have to be serialized back to anyway.
 */
export type NotificationTimeZoneState = Readonly<{
    utcDiff: string;
    utcSign: '+' | '-';
    name: string;
}>;

/**
 * What the device recorded about a notification's confirmation having been completed. Kept apart
 * from the notification entries (see {@link NotificationCollectionState.completions}), so the record
 * outlives the notification: a confirmed notification disappears from the backend's list, and may
 * come back without CRM if its confirmation failed to process there — this is what still tells the
 * app it was submitted.
 */
export type NotificationCompletionState = Readonly<{
    /** the status the notification was completed with */
    status: string;
    /** when the confirmation was completed, in epoch milliseconds */
    timestampCompleted: number;
}>;

/** A completion record together with the notification it belongs to, as the completion map holds it. */
export type NotificationCompletionRecordState = Readonly<
    NotificationCompletionState & {
        notificationId: NotificationIdState;
    }
>;

/**
 * Completion records keyed by `NotificationIdentifierModel.toString()` — the key the legacy
 * `NotificationCompletedListModel` uses, so the two convert without re-keying (see
 * `completionKeyOf`).
 */
export type NotificationCompletionsState = Readonly<Record<string, NotificationCompletionRecordState>>;

/**
 * One notification, flat: the fields are the ones the app works with, not the ones `NdpHeaderSet`
 * sends. `odataToNotificationState` produces it when the payload arrives — numbers parsed, the
 * `{ results: [] }` wrappers gone, every expanded row converted to the app's own type (see
 * `notification-entities.state.ts`), the identifier kept once, the addresses moved out — and it holds
 * everything about the notification the device knows, which is what lets an offline boot serve the
 * notification from the persisted cache alone.
 *
 * Three kinds of field live here, deliberately side by side rather than in sub-objects:
 * - the notification's own data, which only a backend load changes;
 * - the addresses, reduced to {@link customerAddressId} / {@link addressIds} links into the address
 *   slice, which owns every address the app knows;
 * - the status this device recorded ({@link NotificationStatusFields}), which the backend knows
 *   nothing about.
 *
 * The notification's identity is in {@link notificationId} and nowhere else: every lookup goes
 * through that identifier, compared field by field with `NotificationStateUtilities.compare`, and
 * the identifier fields the payload repeats on every expanded row are dropped.
 *
 * What is *derived* rather than stored — and therefore lives in an explicit converter next to this
 * file, not in a field:
 * - everything read out of the long texts (`notification-longtext.converters.ts`): the joined texts,
 *   the shipping priorities, the source replacements, the radioactive tools, the punchlists, the
 *   partner e-mail addresses. The lines are what the backend sent, so the lines are what is stored;
 *   a reader that wants one of those values calls the converter for it.
 * - everything that depends on config (`isRemoteSupport`, `isApplicationSpecialist`,
 *   `isPaceFormsEnabled`, the trouble text): storing it would freeze a config value into a
 *   notification, so the converters take it as an argument and the caller reads it from the config
 *   slice.
 * - the planned window and the malfunction start as instants: turning a SAP date/time pair into one
 *   goes through the device's own time zone, and freezing that at load time would move the
 *   notification's day for a technician who travels. Both stay in their string form.
 * - the customer's display name, which is the four name lines of the address the notification links
 *   to (`customerNameOf`).
 */
export type NotificationState = Readonly<{
    notificationId: NotificationIdState;

    // ---- what the notification is ----
    functionalLocation: string;
    /** `MessagetypeCSE`; the legacy model calls it `mType`. */
    messageType: string;
    companyCode: string;
    notificationType: ConfigConstants.NotificationTypes;
    /** In-vivo / in-vitro, `UNKNOWN` for anything the backend sends that is neither. */
    processIndicator: ConfigConstants.PROCESS_INDICATOR;
    /** Every user status code set on the notification, concatenated — so read it with `includes`. */
    userStatus: ConfigConstants.USER_STATUS;
    taskGroup: string;
    /** The `Notification` field, i.e. the notification number of the maintenance request. */
    mqQmnum: string;
    /** `SalesDocType`. */
    orderType: string;
    /** `ActivityNo`, parsed; `undefined` when the backend sent none. */
    activityNumber: number | undefined;
    salesOrg: string;
    /** `LanguageId`, defaulted to `'EN'` when the backend sent none. */
    languageId: string;
    languageText: string;
    deliveryType: string;
    /** Equipment effect code, driving the card's border color and arrow icon. */
    effect: string;
    anlInaktCode: string;
    assigned: string;
    dispatcher: string;
    /** `NdpVersion`, parsed; `undefined` when the backend sent none. */
    ndpVersion: number | undefined;
    /** `PreClarified`, parsed; `undefined` when the backend sent none. */
    preclarified: number | undefined;
    /** `RepairLimit`, parsed; `undefined` when the backend sent none. */
    repairLimit: number | undefined;
    /** The status the backend reports, DISP (dispatched) when it sent none. */
    backendStatusCode: NOTIFICATION_STATUS_CODE;

    // ---- when it is planned, in SAP's own date/time form (see the type's doc comment) ----
    plannedStartDate: string;
    plannedStartTime: string;
    plannedEndDate: string;
    plannedEndTime: string;
    startMalfunctionDate: string;
    startMalfunctionTime: string;
    timeZone: NotificationTimeZoneState;

    // ---- who it is for ----
    /** The customer address, in the address slice — see the type's doc comment. */
    customerAddressId: string;
    /** The notification's `AddressSet`, in the address slice. */
    addressIds: string[];
    phoneRemote: string;
    /** `CustPONo`, verbatim: the `'n/a'` a missing one is displayed as is the view's business. */
    customerPurchaseOrderNumber: string;
    /** Contact name and phone number in one field, separated by `/`. */
    notificationText: string;
    clarifyText: string;
    clarifyText1: string;

    // ---- the long texts, line by line, in the order the backend sent them ----
    // Every value read out of these is derived, not stored — see the type's doc comment and
    // `notification-longtext.converters.ts`. Each line carries the `textId` its text is
    // distinguished by (`'QMEL'`, `'ZFAD'`, `'ZPR<effect>'`, …), the `lineNumber` it is ordered by,
    // and the notification/task number it was written on — for every set but `historyLines` that is
    // just this notification's own identifier, but a `historyLines` line points at a *previous*
    // notification, which is why the number travels with the line instead of being assumed.
    /** The notification long text: the detail text (`QMEL`), the shipping priorities, the radioactive tools. */
    notificationLines: NotificationLongTextLineState[];
    objectInformationLines: NotificationLongTextLineState[];
    preclarificationLines: NotificationLongTextLineState[];
    historyLines: NotificationLongTextLineState[];
    hotsiteLines: NotificationLongTextLineState[];
    /** The lines the internal-info model is built from. */
    internalInfoLines: NotificationLongTextLineState[];
    /** The issue-report long text. Only NGM AA sends it, so it is empty everywhere else. */
    issueReportLines: NotificationLongTextLineState[];
    rentedMaterialLines: NotificationLongTextLineState[];
    taxiLines: NotificationLongTextLineState[];
    /** The source-replacement lines (`SORC`). Only sent when source replacement is enabled. */
    sourceReplacementLines: NotificationLongTextLineState[];

    // ---- the expanded entity sets, converted, in the order the backend sent them ----
    // The row types are the app's own, not the payload's — see `notification-entities.state.ts`.
    // Sorting is left to the reader, since the payload's order is what the slice keeps: the legacy
    // model orders the sub-models it builds by their `position`, which `sortedByPosition` does here.
    codes: NotificationCodeState[];
    /** The contract information, row by row; the sections it describes are derived (`contractSectionsOf`). */
    contracts: NotificationContractRowState[];
    /** Where material ordered for the notification is delivered. */
    dropOffPoints: NotificationDropOffPointState[];
    emails: NotificationEmailState[];
    equipments: NotificationEquipmentState[];
    history: NotificationHistoryEntryState[];
    parts: NotificationPartState[];
    participants: NotificationParticipantState[];
    /** The notification's tasks, i.e. the statuses the backend recorded for it. */
    tasks: NotificationTaskState[];
    /** The installation-paperwork questions, only sent when installation paperwork is enabled. */
    paperwork: NotificationPaperworkQuestionState[];
    /** `EquipmentCount`, falling back to the number of equipments the backend actually sent. */
    equipmentCount: number;
    formsCount: number;

    // ---- the status this device recorded, see NotificationStatusFields ----
    escalated: boolean;
    cancelled: string;
    unread: boolean;
    status: NotificationStatusRecordState[];
    /**
     * `true` once the user has confirmed this notification's cancellation. Sits on the
     * notification rather than in a single slot on the collection, so several cancelled
     * notifications can carry a confirmation at the same time.
     */
    cancellationConfirmed: boolean;
}>;

/**
 * The names of the status fields the legacy `NotificationStatusModel` carries — see
 * {@link NotificationStatusModelFields}.
 */
type NotificationStatusModelFieldName = 'escalated' | 'cancelled' | 'unread' | 'status';

/**
 * The status fields the legacy `NotificationStatusModel` carries, i.e. the ones a caller of
 * `NotificationStatusInterface` can round-trip through that model — see
 * `notificationStatusModelToFields`. Split out from {@link NotificationStatusFields} so that a
 * bulk write coming from the model cannot silently clear the two fields the model has no place for.
 */
export type NotificationStatusModelFields = Pick<NotificationState, NotificationStatusModelFieldName>;

/**
 * The status a notification has acquired on this device, as opposed to what the backend sent: the
 * statuses it has been through, the four indicator flags and whether the user confirmed its
 * cancellation. Persisted with the entry it belongs to
 * and carried over across loads (see `carryOverStatusFields`), since the backend does not know
 * about it.
 */
export type NotificationStatusFields = Pick<
    NotificationState,
    NotificationStatusModelFieldName | 'cancellationConfirmed'
>;

/**
 * The half of a notification a backend load produces: everything except the status this device
 * recorded. What `odataToNotificationState` returns, and what `carryOverStatusFields` completes
 * with the status the previous load left behind.
 */
export type NotificationDataFields = Omit<NotificationState, keyof NotificationStatusFields>;

/**
 * Marks which backend load the entry collection reflects. `sequence` counts completed loads, so a
 * reader can tell one load from the next without diffing entries; `isAppStart` carries the
 * `initNotification({ isAppStart })` flag through, so that readers who only care about *changes* to
 * the list can ignore the very first load.
 */
/** A status the load's reconciliation appended to an entry, for the load to report. */
export type NotificationStatusAdded = Readonly<{ id: NotificationIdState; status: NotificationStatusRecordState }>;

/**
 * A status the device recorded whose window the backend's task for the same code contradicts. The
 * load overwrites `recorded` with `task`; the difference is reported because it means the device's
 * prediction of the backend (see `notification-status.rules.ts`) was wrong.
 */
export type NotificationStatusDiscrepancy = Readonly<{
    id: NotificationIdState;
    recorded: NotificationStatusRecordState;
    task: NotificationStatusRecordState;
}>;

export type NotificationLoadMarker = Readonly<{
    sequence: number;
    isAppStart: boolean;
    /**
     * What the load's reconciliation decided but cannot carry out itself, for the effects that can:
     * the earliest travel it saw, which the timesheet's start of work is pulled back to, the
     * statuses it appended, which the load logs, and the recorded statuses it corrected from the
     * backend's tasks, which the load warns about. They belong to the load rather than to the
     * notifications, and are ephemeral in the same way the marker is — see
     * `reconcileLoadedStatuses`.
     */
    earliestTravel: string | undefined;
    addedStatuses: NotificationStatusAdded[];
    statusDiscrepancies: NotificationStatusDiscrepancy[];
}>;

export type NotificationCollectionState = Readonly<{
    /**
     * The notifications the last load delivered, ordered by notification start. A load replaces
     * them wholesale, so a notification the backend no longer sends drops out.
     */
    notifications: NotificationState[];
    /**
     * The completion record of every confirmation this device (or, through sync, another one)
     * completed within {@link COMPLETION_RETENTION_MS}, whether or not the backend still sends the
     * notification. Persisted with the entries.
     */
    completions: NotificationCompletionsState;
    loadStatus: LoadStatus;
    /**
     * What went wrong the last time the slice was filled or written: the backend load failed
     * (`notificationFailed`), the persist could not serialize or write the slice, or the read back
     * delivered something the slice cannot restore from (both `notificationStorageSyncError`).
     * `undefined` while nothing has failed.
     *
     * Separate from `loadStatus` because the two answer different questions: a storage sync that
     * failed says nothing about the notifications the slice holds — those are still what the backend
     * last sent — so it records the message without moving the slice out of `Loaded`. Cleared by the
     * next successful load, which is the point at which the slice is authoritative again; a hydrate
     * does not clear it, since a warm cache read does not prove the write that failed would now
     * succeed. Ephemeral, like `lastLoad`: it describes the last attempt, not the notifications, so
     * it is never persisted.
     */
    error: string | undefined;
    /** Notification ids the backend sent that failed to parse into a `NotificationModel`. Ephemeral. */
    incorrectNotificationIds: string[];
    /**
     * The notification whose status `selectCurrentNotificationStatus` — and through it
     * `NotificationNgrxService.notificationStatusSignal` — reports. Ephemeral: set by
     * `initializeNotificationStatus` and never persisted, so `restoredFromStorageReducer` leaves it
     * alone.
     */
    currentNotificationId: NotificationIdState | undefined;
    /**
     * The completed backend load the entries come from, `undefined` until the first one lands. This
     * is what readers observe to learn that the notification list was refreshed (see
     * `notification-updates.ts`). Ephemeral — hydration restores a cached list, not a load, so
     * `restoredFromStorageReducer` leaves it alone and it is never persisted.
     */
    lastLoad: NotificationLoadMarker | undefined;
    /**
     * Counts the cancellations the user confirmed on this device, `undefined` until the first one.
     * The one change to the list that no load reports — the cancellation dialog writes the status
     * and then the notification is gone from the next fetch — so it gets a marker of its own, read
     * the same way `lastLoad` is (see `notification-updates.ts`). Ephemeral, like `lastLoad`.
     */
    lastCancellation: number | undefined;
}>;

/**
 * The per-notification fields that a `NotificationModel` built from an entry has to be completed
 * with, because the model cannot derive them from the stored notification alone — read through
 * `selectNotificationPropertiesSnapshotById`, written through `applyNotificationPropertiesSnapshot`.
 */
export type NotificationPropertiesSnapshot = Readonly<{
    dispOrEtaStart: moment.Moment | undefined;
    dispOrEtaEnd: moment.Moment | undefined;
    updatedStatus: NotificationStatus | undefined;
    isEscalated: boolean;
    isCancelled: string;
    isUnread: boolean;
}>;

export const initialNotificationCollectionState: NotificationCollectionState = {
    notifications: [],
    completions: {},
    loadStatus: LoadStatus.Idle,
    error: undefined,
    incorrectNotificationIds: [],
    currentNotificationId: undefined,
    lastLoad: undefined,
    lastCancellation: undefined,
};

/**
 * The status a notification has before anything has happened to it on this device: not escalated,
 * not cancelled, unread, not paused, no statuses recorded and no cancellation confirmed. Single source of truth for those defaults — a freshly-fetched entry starts from it,
 * and hydrated entries stored before a field moved onto the entry are completed with it.
 */
export function defaultNotificationStatusFields(): NotificationStatusFields {
    return {
        escalated: false,
        cancelled: '',
        unread: true,
        status: [],
        cancellationConfirmed: false,
    };
}

/**
 * The entry for a freshly-converted notification: its postprocessed data and no recorded status
 * yet. Single source of truth for that shape — the converters and the reducer build
 * entries through this factory rather than duplicating the object literal.
 *
 * @param data the postprocessed notification, from `odataToNotificationState`
 */
export function createDefaultNotificationEntry(data: NotificationDataFields): NotificationState {
    return { ...data, ...defaultNotificationStatusFields() };
}

/**
 * Fallback used where a notification is known but has, in principle, no recorded status yet —
 * e.g. `notificationStatusSignal` before any notification has been set as current. Was the
 * `DEFAULT_STATUS` literal on `NotificationNgrxService`; moved here per the "default field
 * value" rule so it lives alongside the other state-layer factories.
 */
export function createDefaultNotificationStatus(): NotificationStatus {
    return { statusCode: NOTIFICATION_STATUS_CODE.DISP, startTime: '', endTime: '' };
}
